
import { Cpu, Lock, Fingerprint, ArrowRight, ExternalLink, Sparkles, Database, Activity, Layers } from "lucide-react";

interface ObliqueKeycardProps {
  onLogin: () => void;
  onSignup: () => void;
  onDemoMode: () => void;
}

export function ObliqueKeycard({ onLogin, onSignup, onDemoMode }: ObliqueKeycardProps) {
  return (
    <div className="lg:col-span-8 flex flex-col gap-6">
      {/* The Main Weird Oblique Keycard Slab */}
      <div className="relative border-2 border-stone-700/80 bg-gradient-to-br from-stone-900/95 via-[#161412]/95 to-stone-950/95 backdrop-blur-2xl p-6 sm:p-10 shadow-2xl transition-all duration-500 lg:-rotate-1 lg:hover:rotate-0 origin-top-left">
        {/* Shimmering Holographic Security Foil Tape */}
        <div
          className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-400 via-rose-400 via-emerald-400 to-cyan-400 opacity-80"
          style={{ backgroundSize: "200% auto" }}
        />

        {/* Hardware Bolt Accents in Corners */}
        <div className="absolute top-3 left-3 text-stone-600 font-mono text-xs select-none">
          ⬡
        </div>
        <div className="absolute top-3 right-3 text-stone-600 font-mono text-xs select-none">
          ⬡
        </div>

        {/* Physical Keycard Header: Microchip + Org ID */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-stone-800/80">
          <div className="flex items-center gap-3">
            {/* Embossed Gold Contact Microchip */}
            <div className="w-12 h-9 bg-gradient-to-tr from-amber-600 via-amber-400 to-yellow-200 p-[1px] shadow-md flex items-center justify-center relative">
              <div className="w-full h-full bg-[#1c1813] flex flex-col justify-between p-1">
                <div className="flex justify-between">
                  <div className="w-2.5 h-1.5 bg-amber-400/80" />
                  <div className="w-2.5 h-1.5 bg-amber-400/80" />
                </div>
                <div className="flex justify-center">
                  <Cpu className="w-3.5 h-3.5 text-amber-300" />
                </div>
                <div className="flex justify-between">
                  <div className="w-2.5 h-1.5 bg-amber-400/80" />
                  <div className="w-2.5 h-1.5 bg-amber-400/80" />
                </div>
              </div>
            </div>

            <div>
              <span className="block font-mono text-[11px] tracking-widest text-stone-400 uppercase">
                ORGANIZATION CLEARANCE
              </span>
              <span className="font-serif text-sm font-semibold tracking-wide text-amber-100">
                Silvestre Nosara Residences
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
            <span className="font-mono text-xs text-red-400 font-medium">
              STATUS: UNAUTHORIZED
            </span>
          </div>
        </div>

        {/* Headline & Editorial Copy */}
        <div className="pt-8 pb-6">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 mb-4 bg-stone-800/60 border border-stone-700/60 text-stone-300 font-mono text-[11px] uppercase tracking-widest">
            <Lock className="w-3 h-3 text-amber-400" />
            <span>Curation & Agent Platform</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-white leading-tight">
            Welcome to the <br />
            <span className="italic font-light text-amber-200">Knowledge Archive</span>
          </h1>

          <p className="mt-4 text-stone-300 text-sm sm:text-base leading-relaxed max-w-xl">
            This system houses authoritative operational rules, proprietary neural vector
            embeddings, and autonomous agent conversation traces. An authenticated operator
            session is required to decrypt and interact with these records.
          </p>
        </div>

        {/* The Hero Button & Actions Bar */}
        <div className="pt-4 pb-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          {/* Magnetic High-Impact Login Button */}
          <button
            type="button"
            onClick={onLogin}
            className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-amber-400 hover:bg-amber-300 active:bg-amber-500 text-stone-950 font-bold text-sm uppercase tracking-wider transition-all duration-200 shadow-xl hover:shadow-amber-500/20 active:translate-y-0.5"
          >
            <Fingerprint className="w-5 h-5 text-stone-900 group-hover:scale-110 transition-transform" />
            <span>Sign In with Silvestre Auth</span>
            <ArrowRight className="w-4 h-4 text-stone-900 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Secondary Request Account / Signup Button */}
          <button
            type="button"
            onClick={onSignup}
            className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-stone-900/80 hover:bg-stone-800 border border-stone-700 hover:border-stone-500 text-stone-300 hover:text-white font-mono text-xs uppercase tracking-wider transition"
          >
            <span>Request Operator Access</span>
            <ExternalLink className="w-3.5 h-3.5 text-stone-500" />
          </button>
        </div>

        {/* Sandbox Demo Option Footer */}
        <div className="mt-6 pt-5 border-t border-stone-800/80 flex flex-wrap items-center justify-between gap-3 text-xs font-mono text-stone-500">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>Evaluating the interface or testing offline?</span>
          </div>
          <button
            type="button"
            onClick={onDemoMode}
            className="text-stone-300 hover:text-amber-300 underline font-semibold transition"
          >
            Launch Interactive Demo Sandbox →
          </button>
        </div>
      </div>

      {/* Overlapping Intersecting Satellite Feature Card */}
      <div className="border border-stone-800/90 bg-stone-950/70 backdrop-blur-md p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:rotate-1 lg:hover:rotate-0 transition-transform">
        <div className="flex items-start gap-3 p-3 bg-stone-900/40 border border-stone-800/60">
          <Database className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] font-mono text-stone-400 uppercase">Knowledge Base</div>
            <div className="text-xs font-semibold text-stone-200 mt-0.5">1,420+ Vectors</div>
            <div className="text-[10px] text-stone-500 mt-0.5">pgvector index</div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 bg-stone-900/40 border border-stone-800/60">
          <Activity className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] font-mono text-stone-400 uppercase">Agent Traces</div>
            <div className="text-xs font-semibold text-stone-200 mt-0.5">Temporal Workflows</div>
            <div className="text-[10px] text-stone-500 mt-0.5">Don Silvestre & Concierge</div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 bg-stone-900/40 border border-stone-800/60">
          <Layers className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] font-mono text-stone-400 uppercase">Operational Rules</div>
            <div className="text-xs font-semibold text-stone-200 mt-0.5">Automated Guards</div>
            <div className="text-[10px] text-stone-500 mt-0.5">Invariants & telemetry</div>
          </div>
        </div>
      </div>
    </div>
  );
}
